import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Lock, CheckCircle, AlertCircle } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import axios from "axios";

import { BACKEND_URL as API } from '../utils/apiBase';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) setError(t('resetPassword.invalidLink'));
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError(t('resetPassword.minLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.mismatch'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('resetPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)' }}>
      <Card className="w-full max-w-md p-8 bg-[#1a1a2e]/90 border-[#2a2a4a] backdrop-blur-xl" data-testid="reset-password-card">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">{t('resetPassword.title')}</h1>
          <p className="text-gray-400">{t('resetPassword.subtitle')}</p>
        </div>

        {success ? (
          <div className="text-center space-y-4" data-testid="reset-success">
            <CheckCircle size={48} className="mx-auto text-green-400" />
            <p className="text-green-400 font-medium text-lg">{t('resetPassword.success')}</p>
            <Button onClick={() => navigate("/login")} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="back-to-login-btn">
              {t('resetPassword.backToLogin')}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="reset-error">
                <AlertCircle size={16} /> {error}
              </div>
            )}
            <div>
              <Label className="text-gray-300 mb-2 block"><Lock size={13} className="inline mr-1" />{t('resetPassword.newPassword')}</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('resetPassword.newPasswordPlaceholder')}
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white h-12"
                data-testid="new-password-input"
                required
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block"><Lock size={13} className="inline mr-1" />{t('resetPassword.confirmPassword')}</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white h-12"
                data-testid="confirm-password-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !token}
              className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
              data-testid="reset-submit-btn"
            >
              {loading ? t('resetPassword.resetting') : t('resetPassword.submit')}
            </Button>
            <div className="text-center">
              <button type="button" onClick={() => navigate("/login")} className="text-purple-400 hover:text-purple-300 text-sm underline" data-testid="back-to-login-link">
                {t('resetPassword.backToLogin')}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
